import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Briefcase,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Plus,
} from "lucide-react";
import { MainLayout } from "@/components/layout/main-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import type { Account, Position, Quote } from "@shared/schema";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

function formatCurrency(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatPercent(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "0.00%";
  return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
}

function StatCard({
  title,
  value,
  change,
  icon: Icon,
  loading,
}: {
  title: string;
  value: string;
  change?: number;
  icon: React.ElementType;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-10 rounded-md" />
          </div>
          <Skeleton className="h-8 w-32 mt-3" />
          <Skeleton className="h-4 w-20 mt-2" />
        </CardContent>
      </Card>
    );
  }

  const isPositive = change !== undefined && change >= 0;

  return (
    <Card className="hover-elevate">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{title}</p>
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
        <p className="text-2xl font-bold mt-3" data-testid={`stat-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          {value}
        </p>
        {change !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? "text-chart-1" : "text-destructive"}`}>
            {isPositive ? (
              <ArrowUpRight className="h-4 w-4" />
            ) : (
              <ArrowDownRight className="h-4 w-4" />
            )}
            <span>{formatPercent(change)}</span>
            <span className="text-muted-foreground">today</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PositionRow({ position }: { position: Position }) {
  const unrealizedPL = parseFloat(position.unrealizedPL?.toString() || "0");
  const isPositive = unrealizedPL >= 0;

  return (
    <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-md bg-card flex items-center justify-center border border-border/50">
          <span className="text-xs font-bold text-foreground">
            {position.symbol?.slice(0, 2).toUpperCase()}
          </span>
        </div>
        <div>
          <p className="font-medium">{position.symbol}</p>
          <p className="text-sm text-muted-foreground">
            {position.quantity} shares @ {formatCurrency(position.averagePrice)}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-medium">{formatCurrency(position.marketValue)}</p>
        <p className={`text-sm ${isPositive ? "text-chart-1" : "text-destructive"}`}>
          {isPositive ? "+" : ""}
          {formatCurrency(unrealizedPL)} ({formatPercent(position.unrealizedPLPercent)})
        </p>
      </div>
    </div>
  );
}

function QuoteRow({ quote }: { quote: Quote }) {
  const change = parseFloat(quote.change?.toString() || "0");
  const isPositive = change >= 0;

  return (
    <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0">
      <div>
        <p className="font-medium">{quote.symbol}</p>
        <p className="text-sm text-muted-foreground truncate max-w-[120px]">{quote.name}</p>
      </div>
      <div className="text-right">
        <p className="font-medium">{formatCurrency(quote.lastPrice)}</p>
        <p className={`text-sm ${isPositive ? "text-chart-1" : "text-destructive"}`}>
          {isPositive ? "+" : ""}
          {formatPercent(quote.changePercent)}
        </p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [, navigate] = useLocation();
  const { data: accounts, isLoading: accountsLoading } = useQuery<Account[]>({
    queryKey: ["/api/accounts"],
  });

  const { data: positions, isLoading: positionsLoading } = useQuery<Position[]>({
    queryKey: ["/api/positions"],
  });

  const { data: quotes, isLoading: quotesLoading } = useQuery<Quote[]>({
    queryKey: ["/api/quotes"],
  });

  const totalPortfolioValue = accounts?.reduce(
    (sum, acc) => sum + parseFloat(acc.portfolioValue?.toString() || "0"),
    0
  ) || 0;

  const totalCash = accounts?.reduce(
    (sum, acc) => sum + parseFloat(acc.cashBalance?.toString() || "0"),
    0
  ) || 0;

  const totalDayPL = accounts?.reduce(
    (sum, acc) => sum + parseFloat(acc.dayProfitLoss?.toString() || "0"),
    0
  ) || 0;

  const dayPLPercent = totalPortfolioValue > 0 ? (totalDayPL / totalPortfolioValue) * 100 : 0;

  const chartData = [
    { name: "Mon", value: totalPortfolioValue * 0.96 },
    { name: "Tue", value: totalPortfolioValue * 0.98 },
    { name: "Wed", value: totalPortfolioValue * 0.97 },
    { name: "Thu", value: totalPortfolioValue * 0.99 },
    { name: "Fri", value: totalPortfolioValue },
  ];

  return (
    <MainLayout
      title="Dashboard"
      subtitle="Your trading overview"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="button-refresh">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => navigate("/open-account")} data-testid="button-open-account">
            <Plus className="h-4 w-4 mr-2" />
            Open Account
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Portfolio Value"
            value={formatCurrency(totalPortfolioValue)}
            change={dayPLPercent}
            icon={Briefcase}
            loading={accountsLoading}
          />
          <StatCard
            title="Today's P&L"
            value={formatCurrency(totalDayPL)}
            change={dayPLPercent}
            icon={totalDayPL >= 0 ? TrendingUp : TrendingDown}
            loading={accountsLoading}
          />
          <StatCard
            title="Cash Balance"
            value={formatCurrency(totalCash)}
            icon={DollarSign}
            loading={accountsLoading}
          />
          <StatCard
            title="Open Positions"
            value={positions?.length?.toString() || "0"}
            icon={Activity}
            loading={positionsLoading}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-medium">Portfolio Performance</CardTitle>
              <div className="flex gap-2">
                <Badge variant="secondary">1W</Badge>
                <Badge variant="outline">1M</Badge>
                <Badge variant="outline">3M</Badge>
                <Badge variant="outline">1Y</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                      formatter={(value: number) => [formatCurrency(value), "Value"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="hsl(var(--chart-1))"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorValue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium">Market Movers</CardTitle>
            </CardHeader>
            <CardContent>
              {quotesLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center justify-between py-3">
                      <div>
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-3 w-24 mt-1" />
                      </div>
                      <div className="text-right">
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-3 w-12 mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : quotes && quotes.length > 0 ? (
                <div className="divide-y divide-border/50">
                  {quotes.slice(0, 5).map((quote) => (
                    <QuoteRow key={quote.symbol} quote={quote} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No market data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-medium">Your Positions</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <a href="/portfolio">View All</a>
            </Button>
          </CardHeader>
          <CardContent>
            {positionsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-md" />
                      <div>
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-3 w-32 mt-1" />
                      </div>
                    </div>
                    <div className="text-right">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-3 w-16 mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            ) : positions && positions.length > 0 ? (
              <div className="divide-y divide-border/50">
                {positions.slice(0, 5).map((position) => (
                  <PositionRow key={position.id} position={position} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No positions yet. Start trading to see your portfolio here.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
